import { useEffect, useState } from 'react';
import { useApi } from '../../services/api';
import { ClaimStatus } from '@bst-stellar/shared';
import { formatDateTime } from '../../utils/dateFormatter';

export default function ClaimValidation() {
    const api = useApi();
    const [claims, setClaims] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'pending'>('pending');
    const [selectedClaim, setSelectedClaim] = useState<any | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejecting, setRejecting] = useState(false);

    useEffect(() => {
        loadClaims();
    }, [filter]);

    async function loadClaims() {
        try {
            setLoading(true);
            const result = await api.getClaims(filter === 'pending' ? 'pending' : undefined);
            setClaims(result.claims || []);
        } catch (error) {
            console.error('Failed to load claims:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleApprove(claimId: string) {
        try {
            await api.approveClaim(claimId);
            await loadClaims();
            setSelectedClaim(null);
        } catch (error) {
            console.error('Failed to approve claim:', error);
            alert('Failed to approve claim');
        }
    }

    function openRejectModal() {
        setRejectionReason('');
        setShowRejectModal(true);
    }

    async function handleReject() {
        if (!selectedClaim) return;
        try {
            setRejecting(true);
            await api.rejectClaim(selectedClaim.id, rejectionReason || undefined);
            await loadClaims();
            setSelectedClaim(null);
            setShowRejectModal(false);
            setRejectionReason('');
        } catch (error) {
            console.error('Failed to reject claim:', error);
            alert('Failed to reject claim');
        } finally {
            setRejecting(false);
        }
    }

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading claims...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Claim Validation</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('pending')}
                        className={`px-4 py-2 rounded ${filter === 'pending'
                            ? 'bg-primary text-white'
                            : 'bg-surface text-text-secondary border border-border hover:bg-background'
                            }`}
                    >
                        Pending ({claims.filter((c) => c.status === 'pending').length})
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded ${filter === 'all'
                            ? 'bg-primary text-white'
                            : 'bg-surface text-text-secondary border border-border hover:bg-background'
                            }`}
                    >
                        All Claims
                    </button>
                </div>
            </div>

            {claims.length === 0 ? (
                <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                    <p className="text-text-secondary text-lg">No claims to review</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Claims List */}
                    <div className="space-y-4">
                        {claims.map((claim) => (
                            <div
                                key={claim.id}
                                onClick={() => setSelectedClaim(claim)}
                                className={`bg-surface rounded-lg shadow p-4 cursor-pointer transition border ${selectedClaim?.id === claim.id
                                    ? 'ring-2 ring-primary border-transparent'
                                    : 'border-border hover:shadow-md'
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-semibold text-text-primary">{claim.itemTitle || 'Bingo Item'}</h3>
                                    <StatusBadge status={claim.status} />
                                </div>
                                <p className="text-sm text-text-secondary mb-2">
                                    By: {claim.userName || 'Unknown User'}
                                </p>
                                <p className="text-sm text-text-muted line-clamp-2">
                                    {claim.description}
                                </p>
                                <p className="text-xs text-text-muted mt-2">
                                    {formatDateTime(claim.createdAt)}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Claim Detail */}
                    <div className="sticky top-4">
                        {selectedClaim ? (
                            <div className="bg-surface rounded-lg shadow p-6 border border-border sticky top-6">
                                <h2 className="text-2xl font-bold mb-4 text-text-primary">Claim Details</h2>

                                <div className="mb-4">
                                    <label className="text-sm font-medium text-text-secondary">Item</label>
                                    <p className="text-lg font-semibold text-text-primary">{selectedClaim.itemTitle}</p>
                                </div>

                                <div className="mb-4">
                                    <label className="text-sm font-medium text-text-secondary">Submitted by</label>
                                    <p className="text-text-primary">{selectedClaim.userName}</p>
                                </div>

                                <div className="mb-4">
                                    <label className="text-sm font-medium text-text-secondary">Description</label>
                                    <p className="text-text-secondary whitespace-pre-wrap">
                                        {selectedClaim.description}
                                    </p>
                                </div>

                                {/* Reference Images */}
                                {selectedClaim.imageUrls && selectedClaim.imageUrls.length > 0 && (
                                    <div className="mb-4">
                                        <label className="text-sm font-medium text-text-secondary mb-2 block">Reference Images</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {selectedClaim.imageUrls.map((url: string, index: number) => (
                                                <a
                                                    key={index}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block group"
                                                >
                                                    <img
                                                        src={url}
                                                        alt={`Reference ${index + 1}`}
                                                        className="w-full h-24 object-cover rounded-lg border border-border hover:ring-2 hover:ring-primary transition-all"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23999" font-size="12" x="50%" y="50%" text-anchor="middle">Invalid</text></svg>';
                                                        }}
                                                    />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Reference URLs */}
                                {selectedClaim.evidenceUrls && selectedClaim.evidenceUrls.length > 0 && (
                                    <div className="mb-4">
                                        <label className="text-sm font-medium text-text-secondary">Reference URLs</label>
                                        <ul className="space-y-1">
                                            {selectedClaim.evidenceUrls.map((url: string, index: number) => (
                                                <li key={index}>
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline text-sm"
                                                    >
                                                        {url}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mb-6">
                                    <label className="text-sm font-medium text-text-secondary">Status</label>
                                    <div className="mt-1">
                                        <StatusBadge status={selectedClaim.status} />
                                    </div>
                                </div>

                                {/* Show rejection reason for rejected claims */}
                                {selectedClaim.status === ClaimStatus.REJECTED && selectedClaim.rejectionReason && (
                                    <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                        <label className="text-sm font-medium text-red-400">Rejection Reason</label>
                                        <p className="text-text-secondary mt-1">{selectedClaim.rejectionReason}</p>
                                    </div>
                                )}

                                {selectedClaim.status === ClaimStatus.PENDING && (
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => handleApprove(selectedClaim.id)}
                                            className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                                        >
                                            ✓ Approve
                                        </button>
                                        <button
                                            onClick={openRejectModal}
                                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                                        >
                                            ✗ Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                                <p className="text-text-secondary">Select a claim to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Rejection Reason Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-surface rounded-lg shadow-xl p-6 w-full max-w-md border border-border">
                        <h3 className="text-xl font-bold mb-4 text-text-primary">Reject Claim</h3>
                        <p className="text-text-secondary mb-4">
                            Provide a reason for rejecting this claim. This will be visible to the employee.
                        </p>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-surface text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                            rows={3}
                            placeholder="Enter rejection reason..."
                        />
                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                className="flex-1 bg-background text-text-secondary px-4 py-2 rounded hover:bg-border border border-border"
                                disabled={rejecting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                                disabled={rejecting}
                            >
                                {rejecting ? 'Rejecting...' : 'Reject Claim'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const styles = {
        pending: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
        approved: 'bg-green-500/20 text-green-400 border border-green-500/30',
        rejected: 'bg-red-500/20 text-red-400 border border-red-500/30',
    };

    return (
        <span
            className={`px-2 py-1 text-xs font-semibold rounded ${styles[status as keyof typeof styles] || 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}
        >
            {status.toUpperCase()}
        </span>
    );
}

